import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { VariableDefault } from '../utils/variableTypes';
import {
    Variable,
    getAffectedPolicies,
} from '../../../services/api/departmentVariablesService';
import { toLabel } from '../utils/variableUtils';
import type { DepartmentUser } from '../setup-wizard/WizardVariableForm';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';
import { VariableRowLabel } from './VariableRowLabel';
import { RefVariablePicker } from './RefVariablePicker';
import { AffectedPoliciesList } from './AffectedPoliciesList';
import { canSaveVariableRow, resolveDisplayValue } from './variableRowUtils';
import { persistVariableDraft } from './variableRowService';

interface VariableRowProps {
    def: VariableDefault;
    savedVariable?: Variable;
    users: DepartmentUser[];
    isCustom?: boolean;
    onSaved: () => Promise<void>;
    onRemove?: () => void;
}


export default function VariableRow({
    def,
    savedVariable,
    users,
    isCustom,
    onSaved,
    onRemove,
}: VariableRowProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({ value: '', refId: undefined as string | undefined, label: '' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteBlocked, setDeleteBlocked] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [nameLocked, setNameLocked] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const label = savedVariable?.label || def.label || toLabel(def.name);
    const displayValue = resolveDisplayValue(def, savedVariable, users);
    const isEmpty = !displayValue;
    const isRequired = !!def.required;

    const hasChanged = isEditing && (
        draft.value !== (savedVariable?.fixedValue || '') ||
        draft.refId !== savedVariable?.refId ||
        (isCustom && draft.label !== label)
    );

    const canSave = hasChanged && canSaveVariableRow({
        def,
        editValue: draft.value,
        editRefId: draft.refId,
        isCustom,
    });

    const userOptions = useMemo(
        () => users.map((u) => ({ id: u.id, label: u.name, description: u.role })),
        [users]
    );

    const enterEdit = () => {
        if (saving) return;
        setDraft({
            value: savedVariable?.fixedValue || '',
            refId: savedVariable?.refId,
            label,
        });
        setIsEditing(true);

        if (isCustom && savedVariable) {
            getAffectedPolicies(def.name)
                .then((policies) => setNameLocked(policies.length > 0))
                .catch(() => setNameLocked(false));
        }
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setDraft({ value: '', refId: undefined, label: '' });
        setNameLocked(false);
    };

    useEffect(() => {
        if (!isEditing) return;

        requestAnimationFrame(() => {
            if (def.field !== 'ref') {
                inputRef.current?.focus();
            }
        });
    }, [def.field, isEditing]);

    const saveField = async () => {
        if (!canSave) return;

        setSaving(true);
        try {
            await persistVariableDraft({
                def,
                savedVariable,
                draft: {
                    value: draft.value,
                    refId: draft.refId,
                    label: draft.label,
                },
                isCustom,
            });

            await onSaved();
            setIsEditing(false);
        } catch {
            toast({ title: t('departmentVariables.errors.failedToSaveField'), variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            cancelEdit();
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            void saveField();
        }
    };

    const renderEditableField = () => {
        if (def.field === 'ref') {
            return (
                <RefVariablePicker
                    options={userOptions}
                    selectedId={draft.refId}
                    freeTextValue={draft.value}
                    canClear={!isRequired}
                    placeholder={t('departmentVariables.variableRow.selectOrTypeName')}
                    searchPlaceholder={t('departmentVariables.variableRow.searchTeamOrType')}
                    emptyText={t('departmentVariables.variableRow.noTeamMembersFound')}
                    onSelect={(id) => setDraft((prev) => ({ ...prev, refId: id, value: '' }))}
                    onSelectFreeText={(text) => setDraft((prev) => ({ ...prev, refId: undefined, value: text }))}
                    onClear={() => setDraft((prev) => ({ ...prev, refId: undefined, value: '' }))}
                />
            );
        }

        if (def.field === 'number') {
            return (
                <Input
                    ref={inputRef}
                    type="number"
                    min={1}
                    placeholder={
                        ('placeholder' in def && def.placeholder) || ''
                    }
                    value={draft.value}
                    onChange={(e) => setDraft((prev) => ({ ...prev, value: e.target.value }))}
                    onKeyDown={handleKeyDown}
                />
            );
        }

        return (
            <Input
                ref={inputRef}
                placeholder={('placeholder' in def && def.placeholder) || ''}
                value={draft.value}
                maxLength={250}
                onChange={(e) => setDraft((prev) => ({ ...prev, value: e.target.value }))}
                onKeyDown={handleKeyDown}
            />
        );
    };

    if (isEditing) {
        return (
            <div className={"grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-b border-subtle last:border-0 items-start"}>
                <VariableRowLabel
                    label={label}
                    required={def.required}
                    isCustom={!!isCustom}
                    editLabel={draft.label}
                    onEditLabel={(val: string) => setDraft((prev) => ({ ...prev, label: val }))}
                    disabled={nameLocked}
                    disabledHint={t('departmentVariables.variableRow.usedByPolicies')}
                    align="start"
                />
                <div className="space-y-2">
                    {renderEditableField()}
                    <div className="flex justify-end gap-2">
                        {isCustom && onRemove && (
                            <>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    disabled={saving}
                                    onClick={() => {
                                        setDeleteBlocked(false);
                                        setDeleteDialogOpen(true);
                                    }}
                                >
                                    {t('departmentVariables.variableRow.delete')}
                                </Button>
                                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                {t('departmentVariables.variableRow.deleteTitle', { label })}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {deleteBlocked
                                                    ? t('departmentVariables.variableRow.deleteBlocked', { label })
                                                    : t('departmentVariables.variableRow.deleteConfirm', { label })}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AffectedPoliciesList
                                            variableName={def.name}
                                            onLoaded={(policies) =>
                                                setDeleteBlocked(policies.length > 0)
                                            }
                                        />
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>{t('departmentVariables.variableRow.cancel')}</AlertDialogCancel>
                                            {!deleteBlocked && (
                                                <AlertDialogAction
                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                    onClick={onRemove}
                                                >
                                                    {t('departmentVariables.variableRow.delete')}
                                                </AlertDialogAction>
                                            )}
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={saving}
                        >
                            {t('departmentVariables.variableRow.cancel')}
                        </Button>
                        {savedVariable ? (
                            <>
                                <Button
                                    size="sm"
                                    disabled={saving || !canSave}
                                    onClick={() => setSaveDialogOpen(true)}
                                >
                                    {saving ? t('departmentVariables.variableRow.saving') : t('departmentVariables.variableRow.save')}
                                </Button>
                                <AlertDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>{t('departmentVariables.variableRow.updateTitle', { label })}</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {t('departmentVariables.variableRow.updateDescription')}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AffectedPoliciesList variableName={def.name} />
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>{t('departmentVariables.variableRow.cancel')}</AlertDialogCancel>
                                            <AlertDialogAction onClick={saveField}>
                                                {t('departmentVariables.variableRow.update')}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </>
                        ) : (
                            <Button
                                size="sm"
                                onClick={saveField}
                                disabled={saving || !canSave}
                            >
                                {saving ? t('departmentVariables.variableRow.saving') : t('departmentVariables.variableRow.save')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-b border-subtle last:border-0 items-center cursor-pointer hover:bg-accent/40 transition-colors group"
            onClick={enterEdit}
        >
            <VariableRowLabel
                label={label}
                required={def.required}
                isCustom={false}
            />
            <div className="flex items-center justify-end gap-2 min-w-0">
                {isEmpty ? (
                    <span className="text-sm text-muted-foreground/50">
                        {t('departmentVariables.variableRow.clickToSet')}
                    </span>
                ) : (
                    <span className="text-sm text-foreground font-medium truncate">
                        {displayValue}
                    </span>
                )}
                <Button
                    variant="outline"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        enterEdit();
                    }}
                >
                    <Pencil className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
}
